import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { hasValidSession, isAuthEntryPath } from '../guards/auth.guard.js';

/**
 * Prevents browser-back from landing on auth/onboarding routes while logged in.
 * Lives at app root so it stays active when the dashboard layout is destroyed.
 */
@Injectable({ providedIn: 'root' })
export class DashboardBackGuardService implements OnDestroy {
  private routerSubscription?: Subscription;
  private popStateListenerAttached = false;
  private lastDashboardUrl = '/dashboard';

  constructor(
    private router: Router,
    private ngZone: NgZone,
  ) {}

  install(): void {
    if (this.popStateListenerAttached) {
      return;
    }

    window.addEventListener('popstate', this.onPopState);
    this.popStateListenerAttached = true;

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects.split('?')[0];
        if (url.startsWith('/dashboard')) {
          this.lastDashboardUrl = url;
        }
        return;
      }

      if (
        event instanceof NavigationStart &&
        event.navigationTrigger === 'popstate' &&
        this.shouldBlockAuthBackNavigation(event.url)
      ) {
        this.redirectToDashboard();
      }
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    if (this.popStateListenerAttached) {
      window.removeEventListener('popstate', this.onPopState);
      this.popStateListenerAttached = false;
    }
  }

  private onPopState = (): void => {
    if (!this.shouldBlockAuthBackNavigation(window.location.pathname)) {
      return;
    }

    this.ngZone.run(() => this.redirectToDashboard());
  };

  private shouldBlockAuthBackNavigation(url: string): boolean {
    return hasValidSession() && isAuthEntryPath(url);
  }

  private redirectToDashboard(): void {
    const currentUrl = this.router.url.split('?')[0];
    const dashboardUrl = currentUrl.startsWith('/dashboard')
      ? currentUrl
      : this.lastDashboardUrl;
    this.router.navigateByUrl(dashboardUrl, { replaceUrl: true });
  }
}
